import React, { useEffect, useState } from "react";
import { PROFILE } from "./landingContent";
import { useLandingCopy } from "./landingCopy";
import { Emphasis, SectionShell } from "./LandingSections";

export interface ProfileValues {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
}

/** Input names, in the order the fields appear in PROFILE.fields. */
const FIELD_NAMES: (keyof ProfileValues)[] = [
  "name",
  "birthDate",
  "birthTime",
  "birthPlace",
];

const AUTOCOMPLETE: Partial<Record<keyof ProfileValues, string>> = {
  name: "name",
  birthPlace: "off",
};

const HAND_OFF_DELAY_MS = 700;

interface ProfileFormProps {
  /** Called shortly after a valid submit so the visitor can read the confirmation. */
  onComplete: (values: ProfileValues) => void;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ onComplete }) => {
  const { tr, headline } = useLandingCopy();
  const [submitted, setSubmitted] = useState<ProfileValues | null>(null);

  useEffect(() => {
    if (!submitted) return undefined;
    const timer = window.setTimeout(() => onComplete(submitted), HAND_OFF_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [submitted, onComplete]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const read = (key: keyof ProfileValues) => String(data.get(key) ?? "").trim();
    setSubmitted({
      name: read("name"),
      birthDate: read("birthDate"),
      birthTime: read("birthTime"),
      birthPlace: read("birthPlace"),
    });
  };

  return (
    <SectionShell id="profile">
      <form className="al-form" onSubmit={handleSubmit} data-reveal>
        <div>
          <div className="al-eyebrow">{tr(PROFILE.eyebrow)}</div>
          <h2 className="al-h2">
            <Emphasis markup={headline("profileT")} />
          </h2>
          <p className="al-lead">{tr(PROFILE.body)}</p>
        </div>

        <div>
          <div className="al-form__fields">
            {PROFILE.fields.map((field, index) => {
              const name = FIELD_NAMES[index];
              const id = `al-profile-${name}`;
              return (
                <div key={name} className="al-field">
                  <label htmlFor={id}>{tr(field.label)}</label>
                  <input
                    id={id}
                    name={name}
                    type={field.type}
                    required={field.required}
                    placeholder={field.placeholder ? tr(field.placeholder) : undefined}
                    autoComplete={AUTOCOMPLETE[name]}
                    disabled={Boolean(submitted)}
                  />
                </div>
              );
            })}
          </div>
          <button
            type="submit"
            className="al-btn al-btn--primary al-form__submit"
            disabled={Boolean(submitted)}
          >
            {tr(PROFILE.cta.label)} ✦
          </button>
          <p
            className={`al-form__note ${submitted ? "al-form__note--ok" : ""}`}
            role="status"
          >
            {submitted ? tr(PROFILE.successMessage) : tr(PROFILE.note)}
          </p>
        </div>
      </form>
    </SectionShell>
  );
};

export default ProfileForm;
